import type { FC } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconCheckOutline18 as Check,
	IconHashtagOutline18 as Hash,
	IconLockOutline18 as Lock,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { useSlackWorkspaceOptions } from "@/lib/slack-workspaces"
import { useThreadsFilterState } from "../threads-filter-state"

// A big Slack Connect setup has thousands of channels, so the query searches and caps.
const CHANNEL_RESULT_LIMIT = 100

export const SlackChannelFilterItems: FC<
	SharedFilterItemsProps & WithQuery
> = ({ onSelect, query }) => {
	const { slackChannelIds, addSlackChannel, removeSlackChannel } =
		useThreadsFilterState()
	const workspaces = useSlackWorkspaceOptions()
	const [channels] = useQuery(
		queries.searchSlackChannels({
			search: query.slice(0, 100),
			limit: CHANNEL_RESULT_LIMIT,
		}),
	)
	const workspaceNames = useMemo(
		() =>
			new Map(workspaces.map((workspace) => [workspace.id, workspace.name])),
		[workspaces],
	)
	// The same Slack Connect channel can be stored for two workspaces; list it once.
	const unique = useMemo(() => {
		const seen = new Set<string>()
		return channels.filter((channel) => {
			if (seen.has(channel.channelId)) return false
			seen.add(channel.channelId)
			return true
		})
	}, [channels])

	return (
		<>
			{unique.map((channel) => {
				const isSelected = slackChannelIds.includes(channel.channelId)
				const ChannelIcon = channel.isPrivate ? Lock : Hash
				return (
					<CommandItem
						key={channel.id}
						value={channel.channelId}
						onSelect={() => {
							if (isSelected) removeSlackChannel(channel.channelId)
							else addSlackChannel(channel.channelId)
							onSelect()
						}}
					>
						<div className="flex w-full min-w-0 items-center gap-2">
							<ChannelIcon className="size-3.5 flex-none text-tertiary" />
							<span className="truncate">{channel.name}</span>
							{workspaces.length > 1 && (
								<span className="flex-none truncate text-xs text-tertiary">
									{workspaceNames.get(channel.slackConnectSettingsId)}
								</span>
							)}
							<span className="flex-1" />
							<Check
								className={cn("h-4 w-4 shrink-0", {
									"opacity-100": isSelected,
									"opacity-0": !isSelected,
								})}
							/>
						</div>
					</CommandItem>
				)
			})}
		</>
	)
}
